import { useEffect, useRef, useState, useCallback } from "react";

const IDLE_TIME = 9.5 * 60 * 1000;
const COUNTDOWN_TIME = 30;

/**
 * Session timeout / idle modal should only apply on Liferay pages whose URL path
 * contains one of these segments (not site-wide when the widget is embedded elsewhere).
 */
export function isBrowserSessionTimeoutScope() {
  if (typeof window === "undefined" || !window.location) return false;
  const p = window.location.pathname || "";
  return (
    p.includes("retirement-plan") ||
    p.includes("payment-success") ||
    p.includes("payment-failure")
  );
}

/**
 * Re-evaluate when the browser URL changes (popstate, SPA navigation, periodic sync).
 */
export function useSessionTimeoutScopeActive() {
  const [active, setActive] = useState(() => isBrowserSessionTimeoutScope());

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sync = () => setActive(isBrowserSessionTimeoutScope());
    sync();
    window.addEventListener("popstate", sync);
    const id = setInterval(sync, 1000);
    return () => {
      window.removeEventListener("popstate", sync);
      clearInterval(id);
    };
  }, []);

  return active;
}

export default function useJourneyInactivity(onRestart, enabled = true) {
  const idleTimer = useRef(null);
  const countdownTimer = useRef(null);
  const resetIdleTimerRef = useRef(() => {});
  const onRestartRef = useRef(onRestart);
  onRestartRef.current = onRestart;

  const [showModal, setShowModal] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_TIME);

  const clearTimers = useCallback(() => {
    clearTimeout(idleTimer.current);
    idleTimer.current = null;
    clearInterval(countdownTimer.current);
    countdownTimer.current = null;
  }, []);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      resetIdleTimerRef.current = () => {};
      setShowModal(false);
      setSecondsLeft(COUNTDOWN_TIME);
      return;
    }

    const startCountdown = () => {
      setSecondsLeft(COUNTDOWN_TIME);
      countdownTimer.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimer.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    const resetIdleTimer = () => {
      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        setShowModal(true);
        startCountdown();
      }, IDLE_TIME);
    };

    resetIdleTimerRef.current = resetIdleTimer;

    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
    ];

    events.forEach((e) => window.addEventListener(e, resetIdleTimer));
    resetIdleTimer();

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
      clearTimeout(idleTimer.current);
      clearInterval(countdownTimer.current);
    };
  }, [enabled, clearTimers]);

  const closeModal = () => {
    clearInterval(countdownTimer.current);
    setShowModal(false);
    resetIdleTimerRef.current();
  };

  const restartJourney = () => {
    clearInterval(countdownTimer.current);
    setShowModal(false);
    onRestartRef.current();
  };

  return {
    showModal,
    secondsLeft,
    closeModal,
    restartJourney,
  };
}
